import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core'
import { ControlContainer, FormArray, FormBuilder, FormGroup, FormGroupDirective, Validators, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms'
import { Availability } from '../domain/Availability'
import { Menu, MenuItem } from '../domain/Consumable'
import { SKU } from '../domain/SKU'
import DineroFactory, { Currency, Dinero } from 'dinero.js'

interface DisplayMenuItem {
    menuItem: MenuItem
    name: string
    description: string | null
    sku: SKU
    price: Dinero
}

@Component({
    selector: 'app-order-selection',
    templateUrl: './order-selection.component.html',
    styleUrls: ['./order-selection.component.sass'],
    viewProviders: [
        {
            provide: ControlContainer,
            useExisting: FormGroupDirective,
        },
    ],
})
export class OrderSelectionComponent implements OnInit, OnChanges {

    @Input() availability!: Availability
    @Input({ required: true }) partySize!: number
    @Input() selectedReasonId!: string | null
    @Output() orderConfirmed = new EventEmitter<void>()
    form: FormGroup
    lineItemsFormArray: FormArray<FormGroup>
    menu: Menu | null = null
    items: DisplayMenuItem[] = []

    characterLimit = 100
    private expanded = new Set<string>()

    constructor(
        private rootFormGroup: FormGroupDirective,
        private fb: FormBuilder
    ) {
        this.form = this.rootFormGroup.form.get('Order') as FormGroup
        this.lineItemsFormArray = this.form.get('lineItems') as FormArray<FormGroup>
    }

    ngOnInit() {
        this.setMenuFromAvailability()
        this.buildDisplayItems()
    }

    ngOnChanges() {
        this.setMenuFromAvailability()
        this.buildDisplayItems()
    }

    incrementItem(item: DisplayMenuItem) {
        if (!this.canIncrement) {
            return
        }
        const existing = this.findLineItemBySku(item.menuItem.skuId)
        if (existing) {
            const qty = existing.get('quantity')!.value + 1
            existing.get('quantity')!.setValue(qty)
            return
        }
        this.lineItemsFormArray.push(this.fb.group({
            name: this.fb.control(item.name, Validators.required),
            productId: this.fb.control(item.menuItem.consumableId, Validators.required),
            skuId: this.fb.control(item.menuItem.skuId, Validators.required),
            amount: this.fb.control(item.price, Validators.required),
            quantity: this.fb.control(1, [Validators.required, Validators.min(1)])
        }))
    }

    decrementItem(item: DisplayMenuItem) {
        const existingIndex = this.findLineItemIndexBySku(item.menuItem.skuId)
        if (existingIndex === -1) { return }
        const control = this.lineItemsFormArray.at(existingIndex)
        const current = control.get('quantity')!.value
        if (current <= 1) {
            this.lineItemsFormArray.removeAt(existingIndex)
        } else {
            control.get('quantity')!.setValue(current - 1)
        }
    }

    getQuantity(item: DisplayMenuItem): number {
        const existing = this.findLineItemBySku(item.menuItem.skuId)
        return existing ? existing.get('quantity')!.value : 0
    }

    isExpanded(item: DisplayMenuItem): boolean {
        return this.expanded.has(item.menuItem.id)
    }

    toggleExpanded(item: DisplayMenuItem): void {
        const id = item.menuItem.id
        if (this.expanded.has(id)) {
            this.expanded.delete(id)
        } else {
            this.expanded.add(id)
        }
    }

    confirmOrder() {
        if (this.validQuantity && this.lineItemsFormArray.valid) {
            this.orderConfirmed.emit()
        }
    }

    get requiredQuantity(): number | null {
        return this.menu?.requiredBookingQuantity(this.partySize) ?? null
    }

    get canIncrement(): boolean {
        return this.requiredQuantity === null || this.totalQuantity < this.requiredQuantity
    }

    get validQuantity(): boolean {
        return this.requiredQuantity === null || this.totalQuantity === this.requiredQuantity
    }

    get totalQuantity(): number {
        return this.lineItemsFormArray.controls.reduce((acc, control) => acc + (control.get('quantity')!.value || 0), 0)
    }

    get totalDinero(): Dinero {
        const zero = DineroFactory({ amount: 0, currency: (this.availability.venue.currencyCode ?? 'GBP') as Currency })
        return this.lineItemsFormArray.controls.reduce((acc, control) => {
            const price: Dinero = control.get('amount')!.value
            const qty: number = control.get('quantity')!.value
            return qty > 0 ? acc.add(price.multiply(qty)) : acc
        }, zero)
    }

    get lineItemGroups(): FormGroup[] {
        return this.lineItemsFormArray.controls
    }

    private setMenuFromAvailability() {
        if (!this.selectedReasonId || !this.availability.productLibrary) {
            this.menu = null
            return
        }
        const reason = this.availability.venue.reasonWithId(this.selectedReasonId)
        if (!reason || !reason.preorderMenuId) {
            this.menu = null
            return
        }
        const menu = this.availability.productLibrary.getMenu(reason.preorderMenuId)
        this.menu = menu?.isPaid ? null : menu
    }

    private buildDisplayItems() {
        if (!this.menu) {
            this.items = []
            this.lineItemsFormArray.clear()
            return
        }
        const consumables = this.availability.productLibrary?.consumables || []
        const allMenuItems = this.menu.menuCourses.flatMap(course => course.menuItems)
        this.items = allMenuItems.flatMap(menuItem => {
            const consumable = consumables.find(c => c.id === menuItem.consumableId)
            const sku = consumable?.getSKUWithId(menuItem.skuId)
            if (!consumable || !sku?.onSale) {
                return []
            }
            return {
                menuItem,
                name: consumable.displayName,
                description: consumable.description,
                sku,
                price: DineroFactory({ amount: sku.price ?? 0, currency: (this.availability.venue.currencyCode ?? 'GBP') as Currency })
            }
        })
        const availableSkuIds = new Set(this.items.map(item => item.sku.id))
        for (let index = this.lineItemsFormArray.length - 1; index >= 0; index--) {
            if (!availableSkuIds.has(this.lineItemsFormArray.at(index).value.skuId)) {
                this.lineItemsFormArray.removeAt(index)
            }
        }
    }

    private findLineItemBySku(skuId: string): FormGroup | null {
        return (this.lineItemsFormArray.controls.find(control => control.get('skuId')?.value === skuId) as FormGroup) || null
    }

    private findLineItemIndexBySku(skuId: string): number {
        return this.lineItemsFormArray.controls.findIndex(control => control.get('skuId')?.value === skuId)
    }
}
